import React from 'react';
import { cn } from '@/lib/utils';

export interface TimeSeriesDataPoint {
  date: string;
  value: number;
}

interface DashboardMetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  dateLabel?: string;
  change?: {
    value: number;
    isPositive: boolean;
  };
  sparklineData?: TimeSeriesDataPoint[];
  icon?: React.ComponentType<{ className?: string }>;
  iconBgColor?: string;
  className?: string;
}

const MiniSparkline: React.FC<{
  data: TimeSeriesDataPoint[];
  isPositive?: boolean;
}> = ({ data, isPositive = true }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d.value).filter((v) => Number.isFinite(v));
  if (values.length === 0) return null;

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;

  const pointCount = data.length;
  const points = data.map((point, index) => {
    const safeValue = Number.isFinite(point.value) ? point.value : minValue;
    const x = pointCount > 1 ? (index / (pointCount - 1)) * 76 + 2 : 40;
    const y = 26 - ((safeValue - minValue) / range) * 22;
    return { x, y };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const strokeColor = isPositive ? 'hsl(var(--success))' : 'hsl(var(--destructive))';

  return (
    <svg width="80" height="28" viewBox="0 0 80 28" className="w-20 h-7">
      <path
        d={linePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const DashboardMetricCard: React.FC<DashboardMetricCardProps> = ({
  title,
  value,
  subtitle,
  dateLabel,
  change,
  sparklineData,
  className,
}) => {
  const isPositiveTrend = change ? change.isPositive :
    (sparklineData && sparklineData.length >= 2
      ? sparklineData[sparklineData.length - 1].value >= sparklineData[sparklineData.length - 2].value
      : true);

  return (
    <div className={cn(
      'border border-border rounded-lg bg-card p-4 shadow-card transition-shadow hover:shadow-sm',
      className
    )}>
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-caption uppercase tracking-wider text-muted-foreground font-medium">{title}</span>
        {dateLabel && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className="text-caption text-muted-foreground/60">{dateLabel}</span>
          </>
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="text-stat text-foreground">{value}</div>
          {subtitle && (
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          )}
          {change && (
            <div className={cn(
              'inline-flex items-center gap-0.5 text-xs font-medium',
              change.isPositive
                ? 'text-success'
                : 'text-destructive'
            )}>
              <span>{change.isPositive ? '↑' : '↓'}</span>
              <span>{Math.abs(change.value)}%</span>
            </div>
          )}
        </div>

        {sparklineData && sparklineData.length > 0 && (
          <div className="flex-shrink-0">
            <MiniSparkline data={sparklineData} isPositive={isPositiveTrend} />
          </div>
        )}
      </div>
    </div>
  );
};
